import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import type { PEOProvider } from '@/types/peo.types';
import type { FilterOptions } from '../filters/PEOFiltersPopover';

export const useProviderFilters = (
  providers: PEOProvider[],
  filterProviders: (providers: PEOProvider[]) => void,
  refetch: () => void
) => {
  const [activeFilters, setActiveFilters] = useState<FilterOptions>({
    certifications: {},
    carveouts: {},
    capabilities: {}
  });
  const { toast } = useToast();

  const applyFilters = useCallback(() => {
    const filteredProviders = providers.filter(provider => {
      // Basic filters
      if (activeFilters.name && !provider.name.toLowerCase().includes(activeFilters.name.toLowerCase())) {
        return false;
      }
      
      if (activeFilters.minEmployees && provider.min_employee_count && 
          provider.min_employee_count < activeFilters.minEmployees) {
        return false;
      }
      
      if (activeFilters.maxEmployees && provider.max_employee_count && 
          provider.max_employee_count > activeFilters.maxEmployees) {
        return false;
      }
      
      if (activeFilters.specialties?.length && provider.specialties) {
        const hasMatch = activeFilters.specialties.some(specialty => 
          provider.specialties.includes(specialty)
        );
        if (!hasMatch) return false;
      }
      
      if (activeFilters.statesServed?.length && provider.states_served) {
        const hasMatch = activeFilters.statesServed.some(state => 
          provider.states_served.includes(state)
        );
        if (!hasMatch) return false;
      }
      
      if (activeFilters.industries?.length && provider.industries_served) {
        const hasMatch = activeFilters.industries.some(industry => 
          provider.industries_served?.includes(industry)
        );
        if (!hasMatch) return false;
      }
      
      if (activeFilters.yearsInBusiness && provider.years_in_business && 
          provider.years_in_business < activeFilters.yearsInBusiness) {
        return false;
      }
      
      if (activeFilters.payrollPlatform && provider.payroll_platform && 
          !provider.payroll_platform.toLowerCase().includes(activeFilters.payrollPlatform.toLowerCase())) {
        return false;
      }
      
      // Medical Carriers filter
      if (activeFilters.medicalCarriers?.length && provider.medical_carriers) {
        const hasMatch = activeFilters.medicalCarriers.some(carrier => 
          provider.medical_carriers?.includes(carrier)
        );
        if (!hasMatch) return false;
      }
      
      // Workers Comp Carriers filter
      if (activeFilters.workersCompCarriers?.length && provider.workers_comp_carriers) {
        const hasMatch = activeFilters.workersCompCarriers.some(carrier => 
          provider.workers_comp_carriers?.includes(carrier)
        );
        if (!hasMatch) return false;
      }
      
      // Renewal Date filter
      if (activeFilters.renewalDateMonth && activeFilters.renewalDateMonth !== 'all' && provider.renewal_date) {
        // Extract month from MM-DD format
        const providerMonth = provider.renewal_date.split('-')[0];
        if (providerMonth !== activeFilters.renewalDateMonth) {
          return false;
        }
      }
      
      // Certification filters
      if (activeFilters.certifications.esacAccredited && !provider.is_esac_accredited) {
        return false;
      }
      
      if (activeFilters.certifications.napeoMember && !provider.napeo_member) {
        return false;
      }
      
      if (activeFilters.certifications.irsCertified && !provider.irs_certified) {
        return false;
      }
      
      if (activeFilters.certifications.audited && !provider.audited) {
        return false;
      }
      
      if (activeFilters.certifications.bbbAccredited && !provider.bbb_accredited) {
        return false;
      }
      
      if (activeFilters.certifications.aicpaSoc && !provider.aicpa_soc) {
        return false;
      }
      
      if (activeFilters.certifications.fullyBondedInsured && !provider.fully_bonded_insured) {
        return false;
      }
      
      // Carveout filters
      if (activeFilters.carveouts.medical && !provider.allows_medical_carveout) {
        return false;
      }
      
      if (activeFilters.carveouts.ancillary && !provider.allows_ancillary_carveout) {
        return false;
      }
      
      if (activeFilters.carveouts.workersComp && !provider.allows_workers_comp_carveout) {
        return false;
      }
      
      if (activeFilters.carveouts.epli && !provider.allows_epli_carveout) {
        return false;
      }
      
      if (activeFilters.carveouts.k401 && !provider.allows_401k_carveout) {
        return false;
      }
      
      // Capability filters
      if (activeFilters.capabilities.mobileApp !== undefined && provider.mobile_app !== activeFilters.capabilities.mobileApp) {
        return false;
      }
      
      if (activeFilters.capabilities.clientPortal !== undefined && provider.client_portal !== activeFilters.capabilities.clientPortal) {
        return false;
      }
      
      if (activeFilters.capabilities.internationalCapabilities !== undefined && 
          provider.international_capabilities !== activeFilters.capabilities.internationalCapabilities) {
        return false;
      }
      
      if (activeFilters.capabilities.startupFriendly !== undefined && 
          provider.startup_friendly !== activeFilters.capabilities.startupFriendly) {
        return false;
      }
      
      if (activeFilters.capabilities.nonprofitExperience !== undefined && 
          provider.nonprofit_experience !== activeFilters.capabilities.nonprofitExperience) {
        return false;
      }
      
      return true;
    });
    
    if (filteredProviders.length === 0) {
      toast({
        title: 'No matching providers',
        description: 'No providers match your filter criteria. Try adjusting your filters.'
      });
    }
    
    console.log(`Filtered providers from ${providers.length} to ${filteredProviders.length}`);
    
    filterProviders(filteredProviders);
  }, [activeFilters, providers, filterProviders, toast]);

  const clearFilters = useCallback(() => {
    setActiveFilters({
      certifications: {},
      carveouts: {},
      capabilities: {}
    });
    refetch();
    toast({
      title: 'Filters cleared',
      description: 'Showing all PEO providers'
    });
  }, [refetch, toast]);

  const hasActiveFilters = Object.values(activeFilters.certifications || {}).some(Boolean) || 
                           Object.values(activeFilters.carveouts || {}).some(Boolean) ||
                           Object.values(activeFilters.capabilities || {}).some(Boolean) ||
                           !!activeFilters.name ||
                           !!activeFilters.minEmployees ||
                           !!activeFilters.maxEmployees ||
                           !!activeFilters.yearsInBusiness ||
                           !!activeFilters.payrollPlatform ||
                           !!activeFilters.renewalDateMonth ||
                           (activeFilters.specialties?.length ?? 0) > 0 ||
                           (activeFilters.statesServed?.length ?? 0) > 0 ||
                           (activeFilters.industries?.length ?? 0) > 0 ||
                           (activeFilters.medicalCarriers?.length ?? 0) > 0 ||
                           (activeFilters.workersCompCarriers?.length ?? 0) > 0;

  return {
    activeFilters,
    setActiveFilters,
    applyFilters,
    clearFilters,
    hasActiveFilters
  };
};
